'use client';
import { hideModal, showToast } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { patchProspect } from '@repo/backoffice-api';
import { Dropdown } from '@repo/ui';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Renders a ChangeTerritoryManager body for a Modal component.
 *
 * @return {React.JSX.Element} The rendered ChangeTerritoryManager component.
 */

export const ChangeTerritoryManagerBody = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { prospectId, territoryManagerId } = useSelector((state: RootState) => state.prospectData);
  const { territoryManagers } = useSelector((state: RootState) => state.territoryManagersData);
  const [loading, setLoading] = useState(false);
  const [selectedTerritoryManagerId, setSelectedTerritoryManagerId] = useState<number | null>(
    territoryManagerId || null,
  );

  const handlePatchTerritoryManager = async () => {
    if (prospectId && selectedTerritoryManagerId) {
      setLoading(true);
      let notAuth = false;
      try {
        const res = await patchProspect({ prospectId, territoryManagerId: selectedTerritoryManagerId });
        if (res.notAuth) {
          notAuth = true;
          dispatch(showToast({ text: 'Usuario no autenticado', kind: 'error' }));
          throw new Error('User not authenticated');
        }
        if (res.softError || res.hardError) {
          dispatch(showToast({ text: 'Hubo un error al actualizar el gerente de territorio', kind: 'error' }));
          throw new Error('Error updating territory manager');
        }
        dispatch(showToast({ text: 'El gerente de territorio ha sido actualizado exitosamente.', kind: 'success' }));
        dispatch(hideModal());
        router.refresh();
      } catch (error) {
        console.error(error);
      } finally {
        if (notAuth) {
          dispatch(hideModal());
          router.push('/login');
          router.refresh();
        }
        setLoading(false);
      }
    }
  };

  const options = (territoryManagers || [])
    .slice()
    .sort((a, b) => (a.description || '').localeCompare(b.description || '', undefined, { sensitivity: 'base' }))
    .map((tm) => ({
      label: `${tm.description}`,
      value: `${tm.id}`,
    }));

  return (
    <Stack gap={4} minWidth="300px">
      <Dropdown
        label="Seleccioná un Gerente de Territorio"
        options={options}
        onChange={(e) => setSelectedTerritoryManagerId(Number(e.target.value))}
        value={`${selectedTerritoryManagerId}`}
      />
      <Stack direction="row" gap={2} width="100%" mt={2}>
        <Button fullWidth variant="outlined" onClick={() => dispatch(hideModal())}>
          Cancelar
        </Button>
        <Button
          fullWidth
          disabled={!selectedTerritoryManagerId}
          onClick={(e) => {
            e.preventDefault();
            handlePatchTerritoryManager();
          }}
        >
          {loading ? (
            <Box sx={{ width: '100%', alignItems: 'center' }}>
              <CircularProgress color="inherit" size={20} />
            </Box>
          ) : (
            <span>Confirmar</span>
          )}
        </Button>
      </Stack>
    </Stack>
  );
};
